import { IGeoCoords, Location, Sublocation } from '@uzurv/clara';
import { AutoCompleteResult } from '@uzurv/core';
import { LocationManagerActions, LocationManagerActionTypes } from './location-manager.actions';

export interface LocationManagerState {
  searchString: string;
  loadingSearchResults: boolean;
  autoCompleteResults: Array<AutoCompleteResult>;
  selectedLocationAutocompleteString: string;
  loadingLocation: boolean;
  loadedLocation: Location;
  mapLoaded: boolean;
  mapCenterCoords: IGeoCoords;
  offsetLocationMode: boolean;
  hoveredLocation: Location | Sublocation;
  selectedLocation: Location;
  addingNewLocation: boolean;
  newLocationName: string;
  sendingNewLocation: boolean;
  deletingSublocation: Sublocation;
  userLocation: IGeoCoords;
  geocodedOffsetLocation: Location;
}

export const initialState: LocationManagerState = {
  searchString: '',
  loadingSearchResults: false,
  autoCompleteResults: [],
  selectedLocationAutocompleteString: '',
  loadingLocation: false,
  loadedLocation: null,
  mapLoaded: false,
  mapCenterCoords: null,
  offsetLocationMode: false,
  hoveredLocation: null,
  selectedLocation: null,
  addingNewLocation: false,
  newLocationName: '',
  sendingNewLocation: false,
  deletingSublocation: null,
  userLocation: null,
  geocodedOffsetLocation: null
};

export const locationManager = (state = initialState, action: LocationManagerActions): LocationManagerState => {
  switch (action.type) {
    case LocationManagerActionTypes.Reset:
      return {
        ...initialState,
        userLocation: state.userLocation
      };

    case LocationManagerActionTypes.Search:
      return {
        ...state,
        autoCompleteResults: state.autoCompleteResults,
        loadingSearchResults: true,
        searchString: action.payload
      };

    case LocationManagerActionTypes.SearchResultsLoaded:
      // eslint-disable-next-line no-case-declarations
      let autoCompleteResults = action.payload;
      if (autoCompleteResults.length === 0) {
        autoCompleteResults = state.autoCompleteResults;
      }
      return {
        ...state,
        loadingSearchResults: false,
        autoCompleteResults: autoCompleteResults
      };

    case LocationManagerActionTypes.LoadSearchResultsFailed:
      return {
        ...state,
        loadingSearchResults: false
      };

    case LocationManagerActionTypes.ClearSearch:
      return {
        ...initialState,
        userLocation: state.userLocation,
        mapCenterCoords: state.userLocation,
        loadedLocation: null,
        searchString: '',
        autoCompleteResults: []
      };

    case LocationManagerActionTypes.SearchResultSelected:
      return {
        ...state,
        loadingLocation: true,
        autoCompleteResults: [],
        selectedLocationAutocompleteString: action.payload
      };

    case LocationManagerActionTypes.LocationLoaded:
      const selectedLocation = new Location();
      Object.assign(selectedLocation, action.payload);
      selectedLocation.sublocations = null;
      return {
        ...state,
        loadingLocation: false,
        loadedLocation: action.payload,
        selectedLocation: selectedLocation,
        mapCenterCoords: {
          lat: action.payload.latitude,
          lng: action.payload.longitude
        }
      };

    case LocationManagerActionTypes.LoadLocationFailed:
      return {
        ...state,
        loadingLocation: false
      };

    case LocationManagerActionTypes.MapLoaded:
      return {
        ...state,
        mapLoaded: true
      };

    case LocationManagerActionTypes.UseOffsetLocation:
      return {
        ...state,
        selectedLocation: null,
        offsetLocationMode: action.payload
      };

    case LocationManagerActionTypes.LocationMouseover:
      return {
        ...state,
        hoveredLocation: action.payload
      };

    case LocationManagerActionTypes.LocationMouseout:
      return {
        ...state,
        hoveredLocation: null
      };

    case LocationManagerActionTypes.LocationSelected:
      return {
        ...state,
        offsetLocationMode: false,
        selectedLocation: action.payload
      };

    case LocationManagerActionTypes.AddNewLocation:
      return {
        ...state,
        offsetLocationMode: false,
        addingNewLocation: true
      };

    case LocationManagerActionTypes.CancelAddNewLocation:
      return {
        ...state,
        addingNewLocation: false
      };

    case LocationManagerActionTypes.MoveMap:
      return {
        ...state,
        mapCenterCoords: action.payload
      };

    case LocationManagerActionTypes.SetNewLocationName:
      return {
        ...state,
        newLocationName: action.payload
      };

    case LocationManagerActionTypes.SubmitNewLocation:
      return {
        ...state,
        sendingNewLocation: true
      };

    case LocationManagerActionTypes.NewLocationInvalid:
      return {
        ...state,
        sendingNewLocation: false
      };

    case LocationManagerActionTypes.NewLocationAccepted:
      const updatedLocation = new Location();
      Object.assign(updatedLocation, state.loadedLocation);
      if (updatedLocation.sublocations === null) {
        updatedLocation.sublocations = [];
      }
      updatedLocation.sublocations.push(action.payload);
      updatedLocation.sublocations.sort((a, b) => a.name.localeCompare(b.name));
      const updatedSelectedLocation = new Location();
      Object.assign(updatedSelectedLocation, updatedLocation);
      updatedSelectedLocation.sublocations = [action.payload];
      return {
        ...state,
        addingNewLocation: false,
        sendingNewLocation: false,
        newLocationName: '',
        loadedLocation: updatedLocation,
        selectedLocation: updatedSelectedLocation
      };

    case LocationManagerActionTypes.SubmitNewLocationFailed:
      return {
        ...state,
        sendingNewLocation: false,
        addingNewLocation: false
      };

    case LocationManagerActionTypes.DeleteSublocation:
      return {
        ...state,
        offsetLocationMode: false,
        deletingSublocation: action.payload
      };

    case LocationManagerActionTypes.DeleteSublocationSuccess:
      const locationWithDeletedSublocation = new Location();
      Object.assign(locationWithDeletedSublocation, state.loadedLocation);
      locationWithDeletedSublocation.sublocations.splice(
        locationWithDeletedSublocation.sublocations.findIndex(sublocation => sublocation.id === state.deletingSublocation.id),
        1
      );
      return {
        ...state,
        deletingSublocation: null,
        loadedLocation: locationWithDeletedSublocation
      };

    case LocationManagerActionTypes.DeleteSublocationFailed:
      return {
        ...state,
        deletingSublocation: null
      };

    case LocationManagerActionTypes.GetUserLocationSuccess:
      return {
        ...state,
        userLocation: action.payload
      };

    case LocationManagerActionTypes.GeocodeOffsetLocationSuccess:
      return {
        ...state,
        geocodedOffsetLocation: action.payload
      }

    default:
      return state;
  }
};
